import { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Typography,
  Progress,
  Alert,
} from 'antd';
import {
  CalendarOutlined,
  FileTextOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { getGalleryItems } from '../services/galleryApi';
import { getEvents } from '../services/api';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAboutContent, selectAboutData } from '../redux/aboutSlice';
const { Title } = Typography;

const Dashboard = () => {
  const dispatch = useDispatch();
  const aboutContent = useSelector(selectAboutData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalEvents: 0,
    activeEvents: 0,
    totalGalleryImages: 0,
    aboutSections: 0,
  });

  const [recentEvents, setRecentEvents] = useState([]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch events
      let events = [];
      try {
        events = await getEvents();
      } catch (eventError) {
        console.error('Error fetching events:', eventError);
      }

      // Fetch gallery items
      let galleryItems = [];
      try {
        galleryItems = await getGalleryItems();
      } catch (galleryError) {
        console.error('Error fetching gallery:', galleryError);
      }

      try {
        dispatch(fetchAboutContent())
      } catch (aboutError) {
      }

      // Calculate active events
      const activeEvents = events.filter(event => event.isActive);

      // Update stats
      const statsUpdate = {
        totalEvents: events.length,
        activeEvents: activeEvents.length,
        totalGalleryImages: Array.isArray(galleryItems) ? galleryItems.length : 0,
        aboutSections: 0,
      };

      setStats(statsUpdate);

      // Set recent events (last 5) sorted by date (most recent first)
      if (Array.isArray(events) && events.length > 0) {
        const recentEventsUpdate = events
          .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date, newest first
          .slice(0, 5)
          .map(event => ({
            key: event._id || Math.random().toString(),
            title: event.title || 'Untitled Event',
            date: event.date ? new Date(event.date).toLocaleDateString() : 'No date',
            status: moment(event.date).isBefore(new Date()) ? 'Completed' : 'Upcoming',
          }));
        setRecentEvents(recentEventsUpdate);
      }

    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      setError(`Failed to fetch dashboard data: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const columns = [
    {
      title: 'Event Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Dashboard</Title>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Total Events"
              value={stats.totalEvents}
              prefix={<CalendarOutlined />}
            />
            <Progress
              percent={Math.round((stats.activeEvents / stats.totalEvents) * 100) || 0}
              size="small"
              status="active"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Active Events"
              value={stats.activeEvents}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Gallery Images"
              value={stats.totalGalleryImages}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="About Sections"
              prefix={<TeamOutlined />}
              value={aboutContent ? 1 : 0}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Recent Events"
        style={{ marginTop: 24 }}
        loading={loading}
      >
        <Table
          columns={columns}
          dataSource={recentEvents}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default Dashboard;
